import { useEffect, useRef } from 'react';

const FOCUSABLE = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function listFocusable(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll(FOCUSABLE))
    .filter((el) => el.getAttribute('aria-hidden') !== 'true');
}

/**
 * Body scroll lock, Escape close, initial focus, Tab trap, focus restore.
 * Effect depends only on `open` so busy toggles do not tear down the trap.
 * Focus is restored only to a still-connected element other than <body> (e.g. Safari does not
 * focus buttons on click); otherwise the optional `returnFocusFallback` picks a remaining entry
 * point (existing callers omit it, which keeps their previous behaviour).
 */
export function useMemberSafetyDialogA11y({ open, onClose, busy = false, panelRef, returnFocusFallback }) {
  const busyRef = useRef(busy);
  const onCloseRef = useRef(onClose);
  const fallbackRef = useRef(returnFocusFallback);
  busyRef.current = busy;
  onCloseRef.current = onClose;
  fallbackRef.current = returnFocusFallback;

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    const focusables = listFocusable(panelRef.current);
    focusables[0]?.focus();

    function onKeyDown(event) {
      if (event.key === 'Escape') {
        if (busyRef.current) return;
        event.preventDefault();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const nodes = listFocusable(panelRef.current);
      if (nodes.length === 0) {
        event.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey) {
        if (document.activeElement === first || !panelRef.current?.contains(document.activeElement)) {
          event.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last || !panelRef.current?.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused?.isConnected && previouslyFocused !== document.body) {
        previouslyFocused.focus?.();
      } else {
        fallbackRef.current?.();
      }
    };
  }, [open, panelRef]);
}
